import React from 'react';
import { snapshot } from 'valtio';
import { AggregationItem, Nullable } from 'common-types';
import SnackbarMessageWithLink from '../../common/components/SnackbarMessageWithLink';
import { Polling } from 'polling-utils';
import { IssueDetails } from '../../issues/common/types/issues-types';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import prService from '../services';
import {
  clearPRComment,
  setAutoFixId,
  setError,
  setFix,
  setIsLoadingCreatePR,
  setLoadingError,
  setLoadingFix,
} from '../store-actions/quick-fix-store-actions';
import QuickFixStore from '../stores/quick-fix-store';
import { FixFile, ViewFix, ViewFixStatus } from '../types/quick-fix-types';
import { resolveSourceControlType } from '../utils/pull-request-utils';
import { User } from '@auth0/auth0-react';
import { logger } from 'logging-utils';
import { openQuickFixPermissionDeniedModal } from '../../issues/active-issues/store-actions/issue-store-actions';
import { ApolloError } from '@apollo/client';

export const createPullRequest = async (
  user: User | undefined,
  onClose: () => void,
): Promise<void> => {
  try {
    if (!user?.email || !user?.name) return;
    setIsLoadingCreatePR(true);
    const { autoFixId, prComment } = snapshot(QuickFixStore);
    const input = {
      prComment: prComment.length ? prComment : undefined,
      committer: { email: user.email, name: user.name },
      autoFixId,
    };
    const pullRequest = await prService.createPullRequest.execute(input);
    if (!pullRequest) {
      openSnackbar('Failed create pull request', {
        variant: 'error',
      });
      return;
    }

    const snackbarMessageWithLink = React.createElement(
      SnackbarMessageWithLink,
      {
        message: 'Pull request created successfully',
        linkTitle: 'Link to PR',
        linkHref: pullRequest.prURL,
      },
    );
    openSnackbar(snackbarMessageWithLink, {
      variant: 'success',
    });
  } catch (error) {
    if (
      error instanceof ApolloError &&
      error.graphQLErrors?.[0].extensions['code'] === '403'
    ) {
      const errorMsg = error.graphQLErrors?.[0]?.message;
      setError(errorMsg);
      openQuickFixPermissionDeniedModal();
      return;
    }

    openSnackbar('Failed create pull request', {
      variant: 'error',
    });
  } finally {
    setIsLoadingCreatePR(false);
    clearPRComment();
    onClose();
  }
};

export const getFix = async (
  selectedIssue: IssueDetails,
  selectedAggs?: AggregationItem[],
) => {
  setLoadingFix(true);
  setLoadingError(false);
  try {
    const fixFiles: FixFile[] = getFixFiles(selectedAggs);

    const autoFixId = await prService.requestViewFix.execute({
      issueId: selectedIssue.issueId,
      sourceControlType: resolveSourceControlType(selectedIssue.app.type),
      owner: selectedIssue.app.organization,
      appId: selectedIssue.app.repoId,
      repo: selectedIssue.app.repoName,
      originBranchName: selectedIssue.app.originBranchName,
      fixFiles,
    });
    if (autoFixId) {
      setAutoFixId(autoFixId);
      viewFixPoller.startPolling();
    } else {
      setLoadingFix(false);
      setLoadingError(true);
    }
  } catch (e) {
    setLoadingFix(false);
    setLoadingError(true);
    logger.error(e);
  }
};

const getFixFiles = (selectedAggs?: AggregationItem[]) => {
  if (!selectedAggs) {
    return [];
  }

  const fixFiles: FixFile[] = [];
  selectedAggs.forEach(agg => {
    const item: FixFile = {
      hashAggId: agg.hashAggId,
      filePath: agg.filePath,
      isLockFile: false,
    };
    fixFiles.push(item);

    if (!agg.lockfile) {
      return;
    }

    const lockFileItem: FixFile = {
      hashAggId: agg.hashAggId,
      filePath: agg.lockfile,
      isLockFile: true,
    };
    fixFiles.push(lockFileItem);
  });

  return fixFiles;
};

const pollViewFix = () => {
  return prService.getViewFix.execute(
    {
      autoFixId: snapshot(QuickFixStore).autoFixId!,
    },
    viewFixPoller.getCallCount() === 0,
  );
};

const viewFixCallback = (response: Nullable<ViewFix>) => {
  if (!response) {
    setLoadingFix(false);
    setLoadingError(true);
    return false;
  }
  if (response.viewFixStatus === ViewFixStatus.Success) {
    setFix(response);
    setLoadingFix(false);
    setLoadingError(false);
    return false;
  }
  if (response.viewFixStatus === ViewFixStatus.Fail) {
    setLoadingFix(false);
    setLoadingError(true);
    return false;
  }
  return true;
};

const errorCallback = () => {
  logger.error('Failed to load snippet for', snapshot(QuickFixStore).autoFixId);
  setLoadingFix(false);
  setLoadingError(true);
};

const viewFixPoller = new Polling<Nullable<ViewFix>>(
  pollViewFix,
  viewFixCallback,
  errorCallback,
  () => {},
);

export const cancelPolling = () => {
  viewFixPoller.cancelPolling();
  setLoadingError(false);
  setLoadingFix(false);
  setIsLoadingCreatePR(false);
};
